import React from 'react';

let sentryModule = null;
let sentryImportPromise = null;
let sentryInitPromise = null;
let sentryInitialized = false;
let configuredDsn = '';

function normalizeDsn(dsn) {
  return typeof dsn === 'string' ? dsn.trim() : '';
}

async function loadSentry() {
  if (!sentryImportPromise) {
    sentryImportPromise = import('@sentry/react')
      .then((module) => {
        sentryModule = module;
        return module;
      })
      .catch((error) => {
        sentryImportPromise = null;
        throw error;
      });
  }

  return sentryImportPromise;
}

function getSentryOptions(dsn) {
  return {
    dsn,
    environment: process.env.NODE_ENV,
    // Free tier: 5K errors/month. 100% errors, 5% perf transactions.
    tracesSampleRate: 0.05,
    // Only in production
    enabled: process.env.NODE_ENV === 'production',
    // Deduplicate identical errors (default integration)
    // Filter noisy browser errors that waste quota
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error exception captured',
      'Network Error',
      'Load failed',
      'ChunkLoadError',
      'Loading chunk',
      'AbortError',
      'TypeError: Failed to fetch',
      'TypeError: NetworkError',
      'TypeError: cancelled',
    ],
    // Only send errors from our domain
    allowUrls: [/debono\.ai/, /localhost/],
    // Drop low-value transactions to save quota
    beforeSendTransaction(event) {
      // Skip health checks and static assets
      if (event.transaction?.includes('/health') || event.transaction?.includes('/static/')) {
        return null;
      }
      return event;
    },
  };
}

export async function initSentry(dsn) {
  configuredDsn = normalizeDsn(dsn);

  if (!configuredDsn) {
    return null;
  }

  if (sentryInitialized && sentryModule) {
    return sentryModule;
  }

  if (!sentryInitPromise) {
    sentryInitPromise = loadSentry()
      .then((Sentry) => {
        if (!sentryInitialized) {
          Sentry.init(getSentryOptions(configuredDsn));
          sentryInitialized = true;
        }
        return Sentry;
      })
      .catch((error) => {
        sentryInitPromise = null;
        console.error('Failed to initialize Sentry:', error);
        return null;
      });
  }

  return sentryInitPromise;
}

async function getInitializedSentry() {
  if (sentryInitialized && sentryModule) {
    return sentryModule;
  }

  if (sentryInitPromise) {
    return sentryInitPromise;
  }

  return null;
}

function logLocalError(error, context) {
  if (context) {
    console.error('Sentry is not initialized; reporting locally:', error, context);
    return;
  }

  console.error('Sentry is not initialized; reporting locally:', error);
}

export async function reportError(error, context) {
  const Sentry = await getInitializedSentry();

  if (Sentry?.captureException && sentryInitialized) {
    try {
      return Sentry.captureException(error, context);
    } catch (captureError) {
      console.error('Failed to report error to Sentry:', captureError);
    }
  }

  logLocalError(error, context);
  return null;
}

export async function setSentryUser(user) {
  const Sentry = await getInitializedSentry();

  if (Sentry?.setUser && sentryInitialized) {
    Sentry.setUser(user);
    return true;
  }

  return false;
}

class PlainErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    this.props.onError?.(error, errorInfo?.componentStack, errorInfo);
  }

  resetError = () => {
    this.setState({ error: null, errorInfo: null });
  };

  renderFallback() {
    const { fallback } = this.props;
    const { error, errorInfo } = this.state;

    if (typeof fallback === 'function') {
      return fallback({
        error,
        componentStack: errorInfo?.componentStack,
        eventId: null,
        resetError: this.resetError,
      });
    }

    if (fallback !== undefined) {
      return fallback;
    }

    return null;
  }

  render() {
    if (this.state.error) {
      return this.renderFallback();
    }

    return this.props.children;
  }
}

export function SentryErrorBoundary({ children, fallback, onError, ...boundaryProps }) {
  const [Boundary, setBoundary] = React.useState(() => (
    sentryInitialized && sentryModule?.ErrorBoundary ? sentryModule.ErrorBoundary : null
  ));

  React.useEffect(() => {
    let cancelled = false;

    if (!configuredDsn) {
      return undefined;
    }

    const pendingSentry = sentryInitPromise || initSentry(configuredDsn);
    pendingSentry.then((Sentry) => {
      if (!cancelled && sentryInitialized && Sentry?.ErrorBoundary) {
        setBoundary(() => Sentry.ErrorBoundary);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!Boundary || !sentryInitialized) {
    return (
      <PlainErrorBoundary fallback={fallback} onError={onError}>
        {children}
      </PlainErrorBoundary>
    );
  }

  return (
    <Boundary fallback={fallback} onError={onError} {...boundaryProps}>
      {children}
    </Boundary>
  );
}
